import React from 'react';
import { Box, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { serverURL } from '../../helpers';

const getImageFileName = (item) => {
  if (!item) return '';
  if (typeof item === 'string') return item;

  return (
    item.src?.src ||
    item.src ||
    item.fileName ||
    item.filename ||
    item.path ||
    item.url ||
    ''
  );
};

const getImageUrl = (item) => {
  const fileName = getImageFileName(item);
  if (!fileName) return '';

  if (/^https?:\/\//i.test(fileName) || fileName.startsWith('/')) {
    return fileName;
  }

  return `${serverURL}/uploaded-files/${fileName}`;
};

const getImageTitle = (item, index) => {
  if (!item || typeof item === 'string') return `Fotografia ${index + 1}`;
  return item.title || item.name || item.originalname || `Fotografia ${index + 1}`;
};

const normalizeImages = (itemData) => {
  if (!Array.isArray(itemData)) return [];

  return itemData
    .map((item, index) => ({
      ...((item && typeof item === 'object') ? item : {}),
      _slideshowId: item?._id || item?.id || `${getImageFileName(item)}-${index}`,
      url: getImageUrl(item),
      title: getImageTitle(item, index),
    }))
    .filter((item) => item.url);
};

export default function ProfileSlideshow({ itemData, autoPlay = true, interval = 4500 }) {
  const images = React.useMemo(() => normalizeImages(itemData), [itemData]);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [isPaused, setIsPaused] = React.useState(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const hasMultipleImages = images.length > 1;
  const activeImage = images[activeIndex];

  React.useEffect(() => {
    setActiveIndex(0);
  }, [images.length]);

  const goToPrevious = React.useCallback(() => {
    setActiveIndex((currentIndex) => (
      currentIndex === 0 ? images.length - 1 : currentIndex - 1
    ));
  }, [images.length]);

  const goToNext = React.useCallback(() => {
    setActiveIndex((currentIndex) => (
      currentIndex === images.length - 1 ? 0 : currentIndex + 1
    ));
  }, [images.length]);

  React.useEffect(() => {
    if (!autoPlay || !hasMultipleImages || isPaused || isDialogOpen) return undefined;

    const timer = window.setInterval(goToNext, interval);
    return () => window.clearInterval(timer);
  }, [autoPlay, hasMultipleImages, interval, isPaused, isDialogOpen, goToNext]);

  if (!images.length) {
    return (
      <Box
        sx={{
          width: '100%',
          maxWidth: 960,
          border: '1px dashed rgba(53, 71, 78, 0.35)',
          borderRadius: 3,
          py: 8,
          px: 2,
          textAlign: 'center',
          color: 'text.primary',
          backgroundColor: 'rgba(255, 255, 255, 0.35)',
        }}
      >
        <Typography variant="body1">Profil zatiaľ neobsahuje žiadne fotografie.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 1160,
        mx: 'auto',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          overflow: 'hidden',
          borderRadius: 4,
          backgroundColor: '#101820',
          boxShadow: '0 18px 50px rgba(53, 71, 78, 0.22)',
        }}
      >
        <Box
          component="img"
          src={activeImage.url}
          alt={activeImage.title}
          onClick={() => setIsDialogOpen(true)}
          sx={{
            display: 'block',
            width: '100%',
            height: { xs: 320, sm: 460, md: 600 },
            objectFit: 'contain',
            cursor: 'zoom-in',
            backgroundColor: '#101820',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            display: 'flex',
            gap: 1,
          }}
        >
          {hasMultipleImages && (
            <IconButton
              aria-label={isPaused ? 'Spustiť slideshow' : 'Pozastaviť slideshow'}
              onClick={() => setIsPaused((value) => !value)}
              sx={{
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.45)',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.65)' },
              }}
            >
              {isPaused ? <PlayArrowIcon /> : <PauseIcon />}
            </IconButton>
          )}

          <IconButton
            aria-label="Otvoriť fotografiu"
            onClick={() => setIsDialogOpen(true)}
            sx={{
              color: '#fff',
              backgroundColor: 'rgba(0, 0, 0, 0.45)',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.65)' },
            }}
          >
            <FullscreenIcon />
          </IconButton>
        </Box>

        {hasMultipleImages && (
          <>
            <IconButton
              aria-label="Predchádzajúca fotografia"
              onClick={goToPrevious}
              sx={{
                position: 'absolute',
                top: '50%',
                left: { xs: 8, sm: 16 },
                transform: 'translateY(-50%)',
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.45)',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.65)' },
              }}
            >
              <ChevronLeftIcon fontSize="large" />
            </IconButton>

            <IconButton
              aria-label="Nasledujúca fotografia"
              onClick={goToNext}
              sx={{
                position: 'absolute',
                top: '50%',
                right: { xs: 8, sm: 16 },
                transform: 'translateY(-50%)',
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.45)',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.65)' },
              }}
            >
              <ChevronRightIcon fontSize="large" />
            </IconButton>
          </>
        )}

        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            px: { xs: 2, sm: 3 },
            py: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            color: '#fff',
            background: 'linear-gradient(to top, rgba(0,0,0,0.72), rgba(0,0,0,0))',
          }}
        >
          <Typography variant="body2" sx={{ textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {activeImage.title}
          </Typography>
          <Typography variant="body2" sx={{ flexShrink: 0 }}>
            {activeIndex + 1} / {images.length}
          </Typography>
        </Box>
      </Box>

      {hasMultipleImages && (
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            pb: 1,
            justifyContent: { xs: 'flex-start', md: 'center' },
          }}
        >
          {images.map((image, index) => (
            <Box
              key={image._slideshowId}
              component="button"
              type="button"
              aria-label={`Zobraziť fotografiu ${index + 1}`}
              onClick={() => setActiveIndex(index)}
              sx={{
                width: 82,
                height: 64,
                p: 0,
                flex: '0 0 auto',
                overflow: 'hidden',
                borderRadius: 2,
                border: index === activeIndex ? '3px solid #35474E' : '1px solid rgba(53, 71, 78, 0.25)',
                cursor: 'pointer',
                backgroundColor: 'transparent',
                opacity: index === activeIndex ? 1 : 0.68,
                transition: 'opacity 150ms ease, border-color 150ms ease',
                '&:hover': { opacity: 1 },
              }}
            >
              <Box
                component="img"
                src={image.url}
                alt={image.title}
                sx={{
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Box>
          ))}
        </Box>
      )}

      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#101820',
            position: 'relative',
          },
        }}
      >
        <IconButton
          aria-label="Zatvoriť fotografiu"
          onClick={() => setIsDialogOpen(false)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 2,
            color: '#fff',
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.65)' },
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ p: { xs: 1, sm: 2 }, backgroundColor: '#101820' }}>
          {activeImage && (
            <Box
              component="img"
              src={activeImage.url}
              alt={activeImage.title}
              sx={{
                display: 'block',
                width: '100%',
                maxHeight: '82vh',
                objectFit: 'contain',
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
